"use client";

import LogoutIcon from "@/assets/icons/logout.svg";
import { useState } from "react";
import { ADMIN_TEXTS } from "@/constants/admin-texts";
import { ResetPasswordDialog } from "@/features/auth";
import { useUpdateProfileMutation } from "@/features/auth/api/profile-queries";
import type { Profile } from "@/features/auth/api/types";
import { useAuth } from "@/hooks/use-auth";
import { notifyError } from "@/lib/notifications";
import { fetchPrivacyPolicyHtml } from "@/lib/privacy-policy";
import { getInitials } from "@/lib/utils";
import { Button, DetailDrawer, DetailDrawerFooterActions, DetailDrawerFooterButton, GlassButton, PrivacyNoticeDialog } from "@/shared/ui";
import classes from "./account-drawer.module.scss";
import { AccountSettingsSection } from "./sections/account-settings-section";
import { MonthlyTargetsSection } from "./sections/monthly-targets-section";
import { PersonalDetailsSection } from "./sections/personal-details-section";
import { StreamingChannelsSection } from "./sections/streaming-channels-section";
import { useAccountDraft } from "./use-account-draft";
const ROLE_LABELS: Record<Profile["accountType"], string> = {
  STREAMER: ADMIN_TEXTS.common.streamer,
  ADMIN: ADMIN_TEXTS.common.admin,
  SUPER_ADMIN: ADMIN_TEXTS.common.superAdmin,
  PLATFORM_ENGINEER: ADMIN_TEXTS.common.platformEngineer,
  TEAM_LEAD: ADMIN_TEXTS.common.teamLead,
  RELATIONSHIP_MANAGER: ADMIN_TEXTS.common.relationshipManager
};
export type AccountDrawerProps = Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: Profile;
  isLoading?: boolean;
}>;
export function AccountDrawer({
  open,
  onOpenChange,
  profile,
  isLoading = false
}: AccountDrawerProps) {
  const {
    mutateAsync: updateProfile,
    isPending: isSaving
  } = useUpdateProfileMutation();
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const {
    logout
  } = useAuth();
  const isStreamer = profile.accountType === "STREAMER";
  const displayName = isStreamer ? profile.streamingUsername ?? profile.name ?? "" : profile.name ?? "";
  const roleLabel = ROLE_LABELS[profile.accountType];
  const {
    isDirty,
    marketingConsent,
    name,
    residentialAddress,
    reset,
    setMarketingConsent,
    setName,
    setResidentialAddress
  } = useAccountDraft(profile);
  const handleClose = () => {
    reset();
    onOpenChange(false);
  };
  const handleSave = async () => {
    if (!isDirty) {
      onOpenChange(false);
      return;
    }
    try {
      await updateProfile({
        ...profile,
        name,
        residentialAddress: isStreamer ? residentialAddress : profile.residentialAddress,
        marketingConsent
      });
      reset();
      onOpenChange(false);
    } catch {
      notifyError("Unable to save profile. Please check your internet connection and try again.");
    }
  };
  const handleViewPrivacyNotice = async () => {
    const html = await fetchPrivacyPolicyHtml();
    setPrivacyPolicy(html);
    setPrivacyOpen(true);
  };
  return <>
      <DetailDrawer opened={open} onClose={handleClose} title={displayName || "—"} avatar={isLoading || isStreamer ? {
      src: profile.saMetadata?.thumbnailUrl,
      alt: displayName,
      initials: getInitials(displayName)
    } : undefined} isSaving={isSaving} footer={<DetailDrawerFooterActions>
            <DetailDrawerFooterButton>
              <GlassButton size="md" fullWidth onClick={handleClose} className={classes.footerActionButton}>
                Cancel
              </GlassButton>
            </DetailDrawerFooterButton>
            <DetailDrawerFooterButton>
              <Button variant="primary" size="md" fullWidth onClick={handleSave} disabled={isSaving || !isDirty} className={classes.footerActionButton}>
                {isSaving ? "Saving…" : "Save"}
              </Button>
            </DetailDrawerFooterButton>
          </DetailDrawerFooterActions>}>
        <div className={classes.sections}>
          <PersonalDetailsSection profile={profile} isLoading={isLoading} name={name} residentialAddress={residentialAddress} roleLabel={roleLabel} onNameChange={setName} onResidentialAddressChange={setResidentialAddress} />
          <MonthlyTargetsSection profile={profile} isLoading={isLoading} />
          <StreamingChannelsSection profile={profile} isLoading={isLoading} />
          <AccountSettingsSection profile={profile} isLoading={isLoading} marketingConsent={marketingConsent} onMarketingConsentChange={setMarketingConsent} onViewPrivacyNotice={handleViewPrivacyNotice} onResetPassword={() => setResetPasswordOpen(true)} />

          <div className={classes.logoutWrap}>
            <GlassButton size="md" fullWidth className={classes.footerActionButton} classNames={{
            section: classes.footerActionButtonIcon
          }} leftIcon={<LogoutIcon width={24} height={24} />} onClick={logout}>
              Logout
            </GlassButton>
          </div>
        </div>
      </DetailDrawer>

      <ResetPasswordDialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen} defaultEmail={profile.email} onSuccess={logout} />

      <PrivacyNoticeDialog opened={privacyOpen} onClose={() => setPrivacyOpen(false)} html={privacyPolicy} />
    </>;
}